import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet";
import { useOutletContext } from "react-router";
import { Grid, PageWrapper } from "components/Containers";
import PageTitle from "components/PageTitle";
import { AddLegacyTermSheetButton, LegacyTermSheetLayout } from "components/TermSheets";
import { Label, Select, SIZE } from "components/Form";
import { CURRENCIES_WITH_FLAGS } from "constants/index";
import { useLegacyTermSheet } from "hooks";

/**
 * LegacyTermSheet
 */
const LegacyTermSheet = () => {
  const intl = useIntl();
  const { selectedSquad } = useOutletContext();
  const {
    buyInCurrency: initialBuyInCurrency,
    sellInCurrency: initialSellInCurrency,
    ...rest
  } = useLegacyTermSheet({ teamId: selectedSquad?.id });
  const [buyInCurrency, setBuyInCurrency] = useState(initialBuyInCurrency);
  const [sellInCurrency, setSellInCurrency] = useState(initialSellInCurrency);

  // When the initial buy in currency changes, update the state
  useEffect(() => {
    setBuyInCurrency(initialBuyInCurrency);
  }, [initialBuyInCurrency]);

  // When the initial sell in currency changes, update the state
  useEffect(() => {
    setSellInCurrency(initialSellInCurrency);
  }, [initialSellInCurrency]);

  // Handle currency change for both buyInCurrency and sellInCurrency
  const onCurrencyChange = (name, value) => (name === "buyInCurrency" ? setBuyInCurrency : setSellInCurrency)?.(value);

  return (
    <PageWrapper>
      <Helmet>
        <title>{intl.formatMessage({ id: "Meta.TermSheets.H1" })}</title>
      </Helmet>
      <Grid>
        <Grid.col start={1} end={13}>
          <PageTitle
            subtitle={<FormattedMessage id="TermSheets.H1" />}
            actionBar={
              <>
                <CurrencyContainer>
                  <BuyingCurrencyLabel>
                    <FormattedMessage id="TermSheets.Legacy.Currency.BuyIn" />
                  </BuyingCurrencyLabel>
                  <StyledSelect
                    name="buyInCurrency"
                    size={SIZE.small}
                    value={buyInCurrency}
                    onChange={onCurrencyChange}
                    options={CURRENCIES_WITH_FLAGS}
                    valueKey="value"
                    labelKey="label"
                    {...rest}
                  />
                </CurrencyContainer>
                <CurrencyContainer>
                  <SellingCurrencyLabel>
                    <FormattedMessage id="TermSheets.Legacy.Currency.SellOut" />
                  </SellingCurrencyLabel>
                  <StyledSelect
                    name="sellInCurrency"
                    size={SIZE.small}
                    value={sellInCurrency}
                    onChange={onCurrencyChange}
                    options={CURRENCIES_WITH_FLAGS}
                    valueKey="value"
                    labelKey="label"
                    {...rest}
                  />
                </CurrencyContainer>
                <AddLegacyTermSheetButton />
              </>
            }
          />
        </Grid.col>
        <Grid.col start={1} end={13}>
          <LegacyTermSheetLayout buyInCurrency={buyInCurrency} sellInCurrency={sellInCurrency} {...rest} />
        </Grid.col>
      </Grid>
    </PageWrapper>
  );
};

const CurrencyContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: anchor-center;
  width: 250px;
`;

const BuyingCurrencyLabel = styled(Label)`
  width: 278px;
  margin-bottom: 0;
`;

const SellingCurrencyLabel = styled(Label)`
  width: 258px;
  margin-bottom: 0;
`;

const StyledSelect = styled(Select)`
  flex-basis: 170px;
`;

export default LegacyTermSheet;
